import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { UserPlus, Loader2 } from "lucide-react";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

// Only redirect to internal same-origin paths after signup — never to
// arbitrary URLs (open-redirect guard).
function safeNext(raw) {
  if (!raw || typeof raw !== "string") return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr(t("auth.password_short"));
      return;
    }
    setLoading(true);
    const res = await register(email, password, name);
    setLoading(false);
    if (res.ok) nav(next, { replace: true });
    else setErr(res.error);
  };

  return (
    <div className="max-w-md mx-auto py-6">
      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#2C4A3E] text-white flex items-center justify-center">
            <UserPlus className="w-5 h-5" />
          </div>
          <h1 className="text-2xl font-bold">{t("auth.create_your_account")}</h1>
        </div>
        <p className="text-[#4A525A] mb-6">{t("auth.create_desc")}</p>

        <GoogleAuthButton testid="register-google" label={t("auth.google_signup")} />

        <div className="my-5 flex items-center gap-3 text-xs text-[#707880]">
          <span className="flex-1 h-px bg-[#E4DDD3]" /> {t("auth.or")} <span className="flex-1 h-px bg-[#E4DDD3]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label={t("auth.your_name")}>
            <input
              data-testid="register-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
              autoComplete="name"
            />
          </Field>
          <Field label={t("auth.email")}>
            <input
              data-testid="register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
              autoComplete="email"
            />
          </Field>
          <Field label={t("auth.password_min")}>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
              autoComplete="new-password"
            />
          </Field>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="register-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="pod-focus w-full bg-[#2C4A3E] hover:bg-[#1F382E] disabled:opacity-70 text-white font-bold py-3 px-6 rounded-xl inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {t("auth.create_account")}
          </button>
        </form>

        <div className="mt-5 text-sm text-[#4A525A]">
          {t("auth.already_have")}{" "}
          <Link to="/login" data-testid="register-goto-login" className="font-bold text-[#C85A32] pod-focus">
            {t("auth.sign_in")}
          </Link>
        </div>

        <p className="mt-4 text-xs text-[#707880]">
          {t("auth.privacy_note")}
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
